import React, { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import {
  FiFileText, FiAlertCircle, FiTrash2, FiSearch, FiChevronLeft,
} from 'react-icons/fi';
import { AmsFileSelection } from '../coiscan/COIScanClientFiles';
import DriverLicenseScanAmsFiles from '../driverlicensescan/DriverLicenseScanAmsFiles';
import MVRScanPreview, { MVRTransaction } from './MVRScanPreview';
import '../commissionscan/CommissionScan.css';

type TabType = 'local' | 'ams';
type Step    = 'client-select' | 'upload' | 'review';

const getToolboxToken = () => localStorage.getItem('toolboxToken') || '';
const getNcToken      = () => localStorage.getItem('amsToken')     || '';

interface Client {
  name: string;
  databaseId: string;
  eMail?: string;
}

interface Props {
  onGoToHistory?: () => void;
}

const MVRScanUpload: React.FC<Props> = ({ onGoToHistory: _onGoToHistory }) => {
  const [step, setStep] = useState<Step>('client-select');

  /* Client search */
  const [clientQuery,    setClientQuery]    = useState('');
  const [clientResults,  setClientResults]  = useState<Client[]>([]);
  const [clientLoading,  setClientLoading]  = useState(false);
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);

  /* Upload */
  const [uploadTab,       setUploadTab]       = useState<TabType>('local');
  const [file,            setFile]            = useState<File | null>(null);
  const [selectedAmsFile, setSelectedAmsFile] = useState<AmsFileSelection | null>(null);
  const [isDragging,      setIsDragging]      = useState(false);
  const [uploadLoading,   setUploadLoading]   = useState(false);
  const [uploadError,     setUploadError]     = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  /* Review */
  const [reviewTx,  setReviewTx]  = useState<MVRTransaction | null>(null);
  const [pdfUrlRaw, setPdfUrlRaw] = useState('');

  /* Client search debounce */
  useEffect(() => {
    if (!clientQuery.trim()) { setClientResults([]); return; }
    const t = setTimeout(() => {
      setClientLoading(true);
      axios.get(`${(process.env.REACT_APP_NOWCERTSAPI     || '')}/api/nowcert/customerlist`, {
        params:  { searchText: clientQuery, nowCertsToken: getNcToken() },
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      })
        .then(r => setClientResults(r.data || []))
        .finally(() => setClientLoading(false));
    }, 400);
    return () => clearTimeout(t);
  }, [clientQuery]);

  const selectClient = useCallback((client: Client) => {
    setSelectedClient(client);
    setClientQuery('');
    setClientResults([]);
  }, []);

  /* File handling — PDF only */
  const pickFile = useCallback((incoming: FileList | File[]) => {
    const first = Array.from(incoming).find(
      f => f.type === 'application/pdf' || /\.pdf$/i.test(f.name),
    );
    if (first) {
      setFile(first);
      setUploadError('');
    } else {
      setUploadError('Only PDF files are accepted for MVR Scan.');
      setTimeout(() => setUploadError(''), 3000);
    }
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    pickFile(e.dataTransfer.files);
  };

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) pickFile(e.target.files);
    e.target.value = '';
  };

  const canScan = !uploadLoading &&
    ((uploadTab === 'local' && !!file) || (uploadTab === 'ams' && !!selectedAmsFile));

  /* Scan API */
  const onStartScan = useCallback(async () => {
    if (!selectedClient) return;
    setUploadLoading(true);
    setUploadError('');
    try {
      const isAms = uploadTab === 'ams';
      const data = new FormData();
      if (!isAms && file) data.append('File', file);
      data.append('ncToken',             getNcToken());
      data.append('insuredId',           selectedClient.databaseId);
      data.append('isFileUploadFromAMS', String(isAms));
      if (isAms && selectedAmsFile) {
        data.append('folderId', selectedAmsFile.folderId);
      }
      data.append('SelectionOptions', JSON.stringify({
        useExistingInsured: true,
        insuredClientId:    selectedClient.databaseId,
        clientName:         selectedClient.name,
        folderId:           selectedAmsFile?.folderId || '',
        fileIds:            selectedAmsFile ? [selectedAmsFile.file.id] : [],
      }));

      const res = await axios.post(`${(process.env.REACT_APP_AcordScan_Url || '')}/mvr_scan`, data, {
        headers: { Authorization: `Bearer ${getToolboxToken()}` },
      });

      if (res.data?.Success === true || res.data?.success === true || res.data?.transactionId) {
        const rawDetails = res.data.driverDetails || '{}';
        const fixedDetails = rawDetails
          .replace(/'/g, '"')
          .replace(/\bTrue\b/g, 'true')
          .replace(/\bFalse\b/g, 'false');

        const tx: MVRTransaction = {
          transactionId:        res.data.transactionId || '',
          fileName:             !isAms ? (file?.name || '') : (selectedAmsFile?.file.name || ''),
          created:              new Date().toISOString(),
          userName:             '',
          status:               2,
          driverDetailsJson:    fixedDetails,
          selectionOptionsJson: JSON.stringify({
            clientName:      selectedClient.name,
            insuredClientId: selectedClient.databaseId,
          }),
          isDriverAddedInAMS:    false,
          isViolationAddedInAMS: false,
        };
        setReviewTx(tx);
        setPdfUrlRaw(res.data.fileUrl || '');
        setStep('review');
      } else {
        setUploadError(res.data?.errorMessage || 'Scan failed. Please try again.');
      }
    } catch (err: any) {
      setUploadError(err.response?.data?.errorMessage || 'Error while scanning document.');
    } finally {
      setUploadLoading(false);
    }
  }, [uploadTab, file, selectedAmsFile, selectedClient]);

  const resetAll = useCallback(() => {
    setStep('client-select');
    setClientQuery(''); setClientResults([]); setSelectedClient(null);
    setUploadTab('local');
    setFile(null); setSelectedAmsFile(null);
    setIsDragging(false); setUploadLoading(false); setUploadError('');
    setReviewTx(null); setPdfUrlRaw('');
  }, []);

  /* ────────────────────────────────────────────────
     STEP 1: Client Select
  ──────────────────────────────────────────────── */
  if (step === 'client-select') {
    return (
      <div className="cs-main" style={{ padding: 0, gap: 14 }}>

        {/* Search box */}
        <div className="cs-client-search">
          <div className="cs-client-search__input-wrap">
            <FiSearch size={15} className="cs-client-search__icon" />
            <input
              className="cs-client-search__input"
              type="text"
              placeholder="Search client name…"
              value={clientQuery}
              autoFocus
              onChange={e => {
                setClientQuery(e.target.value);
                if (selectedClient) setSelectedClient(null);
              }}
            />
            {clientLoading && <span className="cs-client-search__spinner" />}
          </div>

          {clientResults.length > 0 && (
            <ul className="cs-client-dropdown">
              {clientResults.map(c => (
                <li key={c.databaseId} className="cs-client-dropdown__item" onClick={() => selectClient(c)}>
                  <span className="cs-client-dropdown__name">{c.name}</span>
                  {c.eMail && <span className="cs-client-dropdown__email">{c.eMail}</span>}
                </li>
              ))}
            </ul>
          )}
          {clientQuery.trim() && !clientLoading && clientResults.length === 0 && (
            <ul className="cs-client-dropdown">
              <li className="cs-client-dropdown__item" style={{ cursor: 'default', color: 'var(--text-secondary)', opacity: 0.8 }}>
                No clients found
              </li>
            </ul>
          )}
        </div>

        {/* Selected client card + Continue button */}
        {selectedClient && (
          <div style={{
            background: 'var(--surface-glass)', border: '1px solid var(--border-glass)',
            borderRadius: 10, padding: '14px 16px',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
              <div style={{
                width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                background: 'rgba(124,58,237,0.15)', border: '1px solid rgba(124,58,237,0.3)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--accent-purple)', fontWeight: 700, fontSize: 14,
              }}>
                {selectedClient.name.charAt(0).toUpperCase()}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {selectedClient.name}
                </div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 1 }}>
                  Client selected
                </div>
              </div>
            </div>

            <motion.button
              className="cs-btn-start"
              whileHover={{ scale: 1.03 }}
              whileTap={{ scale: 0.97 }}
              onClick={() => setStep('upload')}
              style={{ fontSize: 13, padding: '8px 24px', flexShrink: 0 }}
            >
              Continue →
            </motion.button>
          </div>
        )}
      </div>
    );
  }

  /* ────────────────────────────────────────────────
     STEP 2: Upload
  ──────────────────────────────────────────────── */
  if (step === 'upload') {
    return (
      <div className="cs-main" style={{ padding: 0, gap: 14 }}>

        {/* Top bar — back + client name */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <button
            className="cs-btn-cancel"
            onClick={() => { setStep('client-select'); setFile(null); setSelectedAmsFile(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6 }}
          >
            <FiChevronLeft size={13} /> Back
          </button>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>
            Client: <strong style={{ color: 'var(--accent-purple)' }}>{selectedClient?.name}</strong>
          </span>
        </div>

        {/* Upload tabs */}
        <div className="cs-tabs">
          <button
            className={`cs-tab${uploadTab === 'local' ? ' cs-tab--active' : ''}`}
            onClick={() => { setUploadTab('local'); setSelectedAmsFile(null); }}
          >
            Select Document from Local Drive
          </button>
          <button
            className={`cs-tab${uploadTab === 'ams' ? ' cs-tab--active' : ''}`}
            onClick={() => { setUploadTab('ams'); setFile(null); }}
          >
            Select Document from Client Profile
          </button>
        </div>

        {/* Content */}
        <div className="cs-content">
          {uploadTab === 'local' ? (
            <div
              className={`cs-dropzone${isDragging ? ' cs-dropzone--dragging' : ''}`}
              onDrop={handleDrop}
              onDragOver={e => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={() => setIsDragging(false)}
            >
              {!file ? (
                <>
                  <div className="cs-dropzone__icon"><FiFileText size={28} /></div>
                  <p className="cs-dropzone__title">Drag & Drop your MVR PDF here</p>
                  <p className="cs-dropzone__or">or</p>
                  <motion.button
                    className="cs-browse-btn"
                    onClick={() => fileInputRef.current?.click()}
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    Browse Files
                  </motion.button>
                  <p className="cs-dropzone__types">Allowed: PDF only</p>
                </>
              ) : (
                <div className="cs-file-card">
                  <div className="cs-file-card__icon"><FiFileText size={20} /></div>
                  <div className="cs-file-card__info">
                    <span className="cs-file-card__name">{file.name}</span>
                    <span className="cs-file-card__size">{(file.size / 1024).toFixed(0)} KB</span>
                  </div>
                  <button className="cs-file-card__delete" onClick={() => setFile(null)} title="Remove file">
                    <FiTrash2 size={15} />
                  </button>
                </div>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf"
                style={{ display: 'none' }}
                onChange={handleFileInput}
              />
            </div>
          ) : (
            <div className="cs-client-panel cs-client-panel--browser">
              <DriverLicenseScanAmsFiles
                insuredId={selectedClient!.databaseId}
                clientName={selectedClient?.name}
                onFileSelected={setSelectedAmsFile}
              />
            </div>
          )}
        </div>

        {/* Info banner */}
        <div className="cs-info-banner">
          <FiAlertCircle size={14} />
          <span>
            MVR Scan accepts <strong>PDF files only</strong>. AI will extract driver and violation details.
          </span>
        </div>

        {uploadError && (
          <div style={{ color: '#dc2626', fontSize: 12, padding: '4px 0' }}>{uploadError}</div>
        )}

        {/* Footer */}
        <div className="cs-footer">
          <span className="cs-footer__status">
            {uploadTab === 'local'
              ? (!file ? 'No file selected.' : `${file.name} ready to scan`)
              : (!selectedAmsFile ? 'No file selected.' : `${selectedAmsFile.file.name} ready to scan`)
            }
          </span>
          <div className="cs-footer__actions">
            <button className="cs-btn-cancel" onClick={resetAll} disabled={uploadLoading}>Cancel</button>
            <motion.button
              className="cs-btn-start"
              disabled={!canScan}
              whileHover={{ scale: canScan ? 1.03 : 1 }}
              whileTap={{ scale: canScan ? 0.97 : 1 }}
              onClick={onStartScan}
            >
              {uploadLoading ? <span className="cs-upload-spinner" /> : '✦ Start Scan'}
            </motion.button>
          </div>
        </div>
      </div>
    );
  }

  /* ────────────────────────────────────────────────
     STEP 3: Review
  ──────────────────────────────────────────────── */
  if (step === 'review' && reviewTx) {
    return (
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <MVRScanPreview
          tx={reviewTx}
          inline
          initialPdfUrl={pdfUrlRaw}
          closeLabel="Scan Again"
          onClose={resetAll}
        />
      </div>
    );
  }

  return null;
};

export default MVRScanUpload;

